// src/scenes/GroupDisbursementAnalysis/index.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Button,
  IconButton,
  Divider,
  List,
  MenuItem,
  ListItemButton,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { MoreVert } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { fetchExpensesData } from "../../../src/data/expensesData";
import { companyManagementUrl, userManagementUrl } from "../../config";
import {
  CustomersCard,
  ExpensesCard,
  RevenueCard,
  RevenueTrend,
  SalesCard,
  SalesTrend,
  SoldProductsChart,
} from "../../data/Axios/queries";
import { graphqlQuery } from "../../data/Axios/DynamicService";
import { tokens } from "../../theme";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const base_url = companyManagementUrl.uri;

// Define tour steps for GroupDisbursementAnalysis
const groupDisbursementTourSteps = [
  {
    target: ".group-disbursement-container",
    content: "Welcome to the Group Disbursement Analysis dashboard! Monitor group-based disbursement trends and metrics using filters for regions, branches, and time ranges.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".group-disbursement-region-filter",
    content: "Use the Region filter to view group disbursement data for a specific region. Available for Admins, Regional Managers, and Branch Managers.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-disbursement-branch-filter",
    content: "Select a Branch to analyze group disbursement metrics specific to that branch.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-disbursement-clear-all",
    content: "Click 'Clear All' to reset all filters and date ranges to view default group disbursement data.",
    placement: "bottom",
    condition: (props) => props.userRole !== 5,
  },
  {
    target: ".group-disbursement-time-range",
    content: "Select a time range (e.g., Last 7 Days, This Month) to analyze group disbursement trends over a specific period.",
    placement: "bottom",
  },
  {
    target: ".group-disbursement-custom-dates",
    content: "When 'Custom Range' is selected, use these date pickers to define a specific start and end date for your analysis.",
    placement: "bottom",
    condition: (props) => props.timeRange === "custom",
  },
  {
    target: ".group-disbursement-trend-card",
    content: "The Group Disbursement Trend chart visualizes the amount disbursed and number of loans over the selected time period.",
    placement: "top",
  },
  {
    target: ".group-disbursement-metrics-card",
    content: "The Group Disbursements card shows key metrics like loans and amounts disbursed for today, this month, and this year, compared to previous periods.",
    placement: "top",
  },
];

const GroupDisbursementAnalysis = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const user = parseInt(decodedToken.RoleGroupId);
  const [branchData, setBranchData] = useState([]);
  const [fieldOfficerData, setFieldOfficerData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [fieldOfficerId, setFieldOfficerId] = useState(null);
  const [groupDisbursementDashboardData, setGroupDisbursementDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("last_7_days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [groupDisbursementCollectionTrend, setGroupDisbursementCollectionTrend] = useState([]);
  const [dateError, setDateError] = useState(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const circleSize = isSmallScreen ? 150 : 200;

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath) && !loading) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, loading]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  // Fetch data on component mount
  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        const regionDataResponse = await fetchJsonDynamic("region", null, null, base_url);
        if (regionDataResponse?.results) {
          const results = JSON.parse(regionDataResponse.results);
          setRegionData(results || []);
        }

        const branchDataResponse = await fetchJsonDynamic("branch", null, null, base_url);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }

        const fieldOfficerDataResponse = await fetchJsonDynamic(
          "allUsersView",
          null,
          null,
          userManagementUrl.uri
        );
        if (fieldOfficerDataResponse?.results) {
          const results = JSON.parse(fieldOfficerDataResponse.results);
          const fieldOfficersWithRole5 = results.filter((user) => user.RoleGroupId === 5);
          setFieldOfficerData(fieldOfficersWithRole5 || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "groupDisbursmentDashboardMetrics",
        "dashboardMetrics",
        {
          companyid: null,
          regionid: regionId,
          branchid: branchId,
        },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const results = JSON.parse(dashboardDataResponse.results);
        setGroupDisbursementDashboardData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "groupDisbursmentTrend",
        "dashboardMetrics",
        {
          companyId: null,
          regionId: regionId,
          branchId: branchId,
          dateRange: timeRange,
          customStartDate: startDate ? startDate.format("YYYY-MM-DD") : null,
          customEndDate: endDate ? endDate.format("YYYY-MM-DD") : null,
        },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const data = JSON.parse(dashboardDataResponse.results);
        setGroupDisbursementCollectionTrend(data || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [regionId, branchId]);

  useEffect(() => {
    // Validate custom date range
    if (timeRange === "custom" && startDate && endDate) {
      if (startDate.isAfter(endDate)) {
        setDateError("Start date cannot be after end date");
        return;
      } else {
        setDateError(null);
      }
    } else {
      setDateError(null);
    }
    fetchData();
  }, [regionId, branchId, timeRange, startDate, endDate]);

  const handleTimeRangeChange = (event) => {
    const selectedRange = event.target.value;
    setTimeRange(selectedRange);
    if (selectedRange !== "custom") {
      setStartDate(null);
      setEndDate(null);
      setDateError(null);
    }
  };

  const handleRegionChange = (event) => {
    const selectedRegionId = event.target.value;
    setRegionId(selectedRegionId);
    setBranchId(null);
  };

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    setBranchId(selectedBranchId);
  };

  const handleClearAll = () => {
    setRegionId(null);
    setBranchId(null);
    setTimeRange("last_7_days");
    setStartDate(null);
    setEndDate(null);
    setDateError(null);
    fetchDashboardData();
    fetchData();
  };

  const paperStyle = {
    p: 1,
    display: "flex",
    alignItems: "center",
    height: 50,
    backgroundColor: `${colors.primary[400]} !important`,
  };

  const selectStyle = {
    height: "100%",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: `${colors.primary[500]} !important`,
    color: "white",
    padding: "0 10px",
    borderRadius: "4px",
    height: "100%",
    marginRight: "10px",
  };

  const regionOptions = regionData.map((region) => ({
    value: region.Id,
    label: region.Name,
  }));

  const branchOptions = branchData
    .filter((branch) => !regionId || branch.RegionId === regionId)
    .map((branch) => ({
      value: branch.Id,
      label: branch.Name,
    }));

  const getPositiveProgressColor = (value) => {
    if (value <= 40) return "red";
    if (value <= 60) return "#d4a106";
    return "green";
  };

  return (
    <Box
      sx={{
        p: 3,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridGap: "16px",
      }}
      className="group-disbursement-container"
    >
      {/* Joyride Tour */}
      <Joyride
        steps={groupDisbursementTourSteps.filter((step) =>
          step.condition ? step.condition({ userRole: user, timeRange }) : true
        )}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Group Disbursement Analysis Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard helps you analyze group-based disbursement trends and metrics. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container spacing={4} marginTop={1}>
          <Grid container spacing={4} marginLeft={1} className="group-disbursement-filters">
            {(user === 1 || user === 2 || user === 3) && regionData.length > 0 && (
              <Grid item xs={12} md={4} className="group-disbursement-region-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Region
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Region...</InputLabel>
                    <Select
                      label="Select Region..."
                      value={regionId || ""}
                      onChange={handleRegionChange}
                      sx={selectStyle}
                      className="group-disbursement-region-select"
                      aria-label="Region Filter"
                    >
                      {regionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {(user === 1 || user === 2 || user === 3) && branchData.length > 0 && (
              <Grid item xs={12} md={4} className="group-disbursement-branch-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Branches
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Branch...</InputLabel>
                    <Select
                      label="Select Branch..."
                      value={branchId || ""}
                      onChange={handleBranchChange}
                      sx={selectStyle}
                      className="group-disbursement-branch-select"
                      aria-label="Branch Filter"
                    >
                      {branchOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {user !== 5 && (
              <Grid item xs={12} md={1} className="group-disbursement-clear-all">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} md={8} className="group-disbursement-trend-card">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 580,
                background: `${colors.primary[400]} !important`,
              }}
              role="region"
              aria-label="Group Disbursement Trend"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    {timeRange.replace("_", " ").toUpperCase()} Group Disbursement Trend
                  </Typography>
                  <Select
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    sx={{ mb: 2 }}
                    className="group-disbursement-time-range"
                    aria-label="Time Range Selector"
                  >
                    <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="this_month">This Month</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>

                  {timeRange === "custom" && (
                    <Box sx={{ display: "flex", gap: 2, mt: 2 }} className="group-disbursement-custom-dates">
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue ? dayjs(newValue) : null)}
                        renderInput={(params) => <TextField {...params} error={!!dateError} />}
                      />
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue ? dayjs(newValue) : null)}
                        renderInput={(params) => <TextField {...params} error={!!dateError} helperText={dateError} />}
                      />
                    </Box>
                  )}
                </Box>
              </LocalizationProvider>

              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={groupDisbursementCollectionTrend}>
                  <defs>
                    <linearGradient id="amountdisbursed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="loans" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="PeriodLabel" label={{ value: "Time", position: "insideBottom", offset: -5 }} />
                  <YAxis yAxisId="left" label={{ value: "Amount Disbursed", angle: -90, position: "insideLeft" }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value: "Loans", angle: -90, position: "insideRight" }} />
                  <Tooltip />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="amountdisbursed"
                    stroke="#8884d8"
                    name="Amount Disbursed"
                    fillOpacity={1}
                    fill="url(#amountdisbursed)"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="loans"
                    stroke="#82ca9d"
                    name="Loans Disbursed"
                    fillOpacity={1}
                    fill="url(#loans)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4} className="group-disbursement-metrics-card">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 580,
                background: `${colors.primary[400]} !important`,
                display: "flex",
                flexDirection: "column",
              }}
              role="region"
              aria-label="Group Disbursement Metrics"
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Group Disbursements</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ border: "3px solid #ddd", borderRadius: "18px", padding: "16px", display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">Today</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: getPositiveProgressColor(parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeToday?.toFixed(2)) || 0) }}
                      >
                        {parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeToday?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedToday || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedToday || 0}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Yesterday</Typography>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedYesterday || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedYesterday || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ border: "3px solid #ddd", borderRadius: "8px", padding: "16px", display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">This Month</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: getPositiveProgressColor(parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeThisMonth?.toFixed(2)) || 0) }}
                      >
                        {parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeThisMonth?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedThisMonth || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedThisMonth || 0}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Last Month</Typography>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedLastMonth || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedLastMonth || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ border: "3px solid #ddd", borderRadius: "8px", padding: "16px", display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">This Year</Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: getPositiveProgressColor(parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeThisYear?.toFixed(2)) || 0) }}
                      >
                        {parseFloat(groupDisbursementDashboardData[0]?.PercentageChangeThisYear?.toFixed(2)) || 0}%
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedThisYear || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedThisYear || 0}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider />
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Last Year</Typography>
                    <Box>
                      <Typography variant="h6" display="inline">
                        {groupDisbursementDashboardData[0]?.LoansDisbursedLastYear || 0}
                      </Typography>
                      <Typography variant="body2" display="inline" sx={{ ml: 1 }}>Loans</Typography>
                      <Typography variant="body2">
                        {groupDisbursementDashboardData[0]?.AmountDisbursedLastYear || 0}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GroupDisbursementAnalysis;